import * as angular from 'angular';
import * as moment from 'moment';

import { ComponentDefinition, Dictionary, IdentifierQuery, IValueViewModel, ObjectUser, RefreshClickData } from '../../../../models';
import { refreshClick } from '../../../../common/events';
import { IIdentifierQueryService, ITranslatedLoggerService } from '../../../../services';

class QueriesStateController implements angular.IComponentController {
    executedQuery: { name: string; count: number };
    formController: angular.IFormController;
    identifiers: IValueViewModel[];
    isLoading: boolean;
    isLoadingResult: boolean;
    loadingMessage: string;
    menu: { isVisible: boolean, property: string, type: string, label: string }[];
    queries: IdentifierQuery[];
    selectedItem: IdentifierQuery;
    users: Dictionary<ObjectUser[]>;

    readonly isEditMode: boolean;

    private _garbages: (() => void)[] = [];
    private _blankQuery: IdentifierQuery = {
        guid: undefined,
        aggregationModes: [],
        name: 'New',
        shortComment: 'Newly created',
        isCreating: true,
        search: '',
        creationUser: '',
        creationDate: undefined,
        lastModifier: '',
        lastModificationDate: undefined
    };

    static $inject = [
        '$scope',
        '$timeout',
        'identifierQueryService',
        'translatedLoggerService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$timeout: angular.ITimeoutService,
        private _identifierQueryService: IIdentifierQueryService,
        private _loggerService: ITranslatedLoggerService
    ) {
    }

    $onInit() {
        this.menu = [
            { isVisible: true, property: 'name', type: 'name', label: 'Name' },
            { isVisible: false, property: 'tagName', type: 'tag', label: 'Tag/Identifier' },
            { isVisible: true, property: 'groupUniqueName', type: 'groupUniqueName', label: 'Group' },
            { isVisible: true, property: 'shortComment', type: 'shortComment', label: 'Short comment' },
            { isVisible: false, property: 'longComment', type: 'longComment', label: 'Long comment' },
            { isVisible: false, property: 'documentation', type: 'documentation', label: 'Documentation' },
            { isVisible: true, property: 'variableTypeName', type: 'type', label: 'Type' },
            { isVisible: true, property: 'variableSubTypeName', type: 'subtype', label: 'Subtype' },
            { isVisible: true, property: 'qualifierName', type: 'qualifier', label: 'Qualifier' },
            { isVisible: true, property: 'unitName', type: 'unit', label: 'Unit' },
            { isVisible: false, property: 'target', type: 'target', label: 'Target' },
            { isVisible: false, property: 'min', type: 'min', label: 'Min' },
            { isVisible: false, property: 'max', type: 'max', label: 'Max' },
            { isVisible: false, property: 'resolution', type: 'resolution', label: 'Resolution' },
            { isVisible: true, property: 'customProperties', type: 'customProperties', label: 'User fields' }
        ];
        this.refresh();
        this._garbages.push(this._$scope.$on(refreshClick, (event, data: RefreshClickData) => this.refresh()));
        this._garbages.push(this._$scope.$watch(() => this.selectedItem, () => {
            if (!this.selectedItem) {
                this.cleanQueries();
            }
        }));
    }

    $onDestroy() {
        this._garbages.forEach(w => w());
    }

    addQuery(data: any) {
        const newQuery = Object.assign({}, this._blankQuery);
        this.queries.push(newQuery);
        this.selectedItem = newQuery;
        this._$timeout(() => {
            if (this.formController) {
                this.formController.$dirty = true;
            }
        });
    }

    delete(query: IdentifierQuery): void {
        this._identifierQueryService
            .delete(query.guid)
            .then(queries => this.queries = queries)
            .catch(() => this._loggerService.logError('Failed to delete query'));
    }

    duplicate(query: IdentifierQuery): void {
        this._identifierQueryService
            .add(query)
            .then(result => {
                this.queries = result.list;
                if (result.message) {
                    this._loggerService.logSuccess(result.message);
                }
            })
            .catch(() => this._loggerService.logError('Failed to duplicate query'));
    }

    // debug purpose for executing request
    executeDebug() {
        const begin = moment(new Date()).subtract(15, 'd').toISOString();
        const end = moment(new Date()).toISOString();
        this._identifierQueryService
            .resolve(this.selectedItem.guid, begin, end)
            .then(data => {
                console.log(data);
            });
    }

    execute(live: boolean) {
        if (this.selectedItem) {
            this.executedQuery = {
                name: this.selectedItem.name,
                count: 0
            };
            this.isLoadingResult = true;
            (live
                ? this._identifierQueryService.preview(this.selectedItem)
                : this._identifierQueryService.execute(this.selectedItem.guid))
                .then(result => {
                    this.identifiers = result;
                    this.executedQuery.count = result.length;
                })
                .finally(() => this.isLoadingResult = false);
        }
    }

    unselect($event: any) {
        if (this.selectedItem && !$event.isDefaultPrevented() && (!this.formController || !this.formController.$dirty)) {
            this.selectedItem = null;
            if (this.formController) {
                this.formController.$setPristine();
            }
        }
    }

    private refresh(): void {
        this.isLoading = true;
        this._identifierQueryService
            .getAll()
            .then(data => {
                this.users = {};
                this.queries = data.map(d => {
                    this.users[d.query.guid] = d.users;
                    return d.query;
                });
            })
            .finally(() => this.isLoading = false);
    }

    private cleanQueries(): void {
        if (this.queries) {
            this.queries = this.queries.filter(query => !query.isCreating);
        }
    }
}

export const wtQueriesState: ComponentDefinition = {
    componentId: 'wtQueriesState',
    controller: QueriesStateController,
    template: require('./queriesState.component.html'),
    controllerAs: 'vm',
    bindings: {
        formController: '=',
        isEditMode: '<',
        queries: '=',
        selectedItem: '=',
    }
};